import {
  SkyAccordion,
  SkyAccordionItem,
  SkyButton,
  SkyCard,
  SkyMessageBar,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useEffect } from "react";
import { Link, Route, Switch, useLocation, useSearch } from "wouter";
import { isMcpWriteIntentId, type McpWriteIntentView } from "@shared/mcp/writeIntents";
import { McpInstanceSwitchNotice } from "@/components/mcp/McpInstanceSwitchNotice";
import { runInstanceLink, withoutInstanceParam } from "@/components/runs/RunInstanceSwitchNotice";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useNow } from "@/hooks/use-now";
import { useDecideMcpWriteIntent, useMcpWriteIntent } from "@/hooks/use-mcp";
import { failureMessage, isApiErrorCode } from "@/lib/api";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import {
  approvePhase,
  coverageSentence,
  outcomeCodeSentence,
  summaryValueText,
  writeReasonSentence,
  writeToolLabel,
  type ApprovePhase,
} from "@/lib/mcp/writeIntentWords";
import { absolutePath, ROUTES } from "@/lib/routes";
import { endedWhen } from "@/lib/runs/runActivity";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import NotFoundPage from "@/pages/not-found";

/**
 * Approve a change an AI app asked to make (ADR 0009 section 6, release 8B).
 *
 * The approve link is the only human control over an AI app's writes. A write that enables or
 * disables a connection, covers more than 20 tables, or comes after ten in an hour on one
 * connection of the app is held until the person who connected the app opens this page, signed
 * in to skyData in that connection's instance, reads what will happen and approves it. Only then
 * does the write run, at once, as that person (the server runs it in process under the grant).
 *
 * The link is `/mcp-approve/<id>?instance=<grant instance>`. `?instance=` never selects: another
 * instance waits for "Switch", and nothing is read under the wrong one. The server answers 404
 * unless the intent is this person's, in this instance, so the page never learns whether
 * someone else's id exists. A link lives 24 hours; after that the AI app prepares the change
 * again. Nothing on this page is a credential: an intent's arguments are refused before the row
 * is written if any field is one (`MCP_CREDENTIAL_REFUSED`).
 */
export default function McpApprovePage() {
  return (
    <Switch>
      <Route path="/:id">{(params) => <McpApproveScreen id={params.id as string} />}</Route>
      <Route>
        <NotFoundPage />
      </Route>
    </Switch>
  );
}

function NoChangeHere({ testId, title }: { testId: string; title: string }) {
  return (
    <SkyMessageBar
      intent="warning"
      layout="multiline"
      testId={testId}
      title={title}
      message="An approval link opens only for the person who connected the AI app, signed in to skyData in the instance the app is connected to. Ask the AI app for a new link if you need one."
    />
  );
}

function McpApproveScreen({ id }: { id: string }) {
  const [location, navigate] = useLocation();
  const search = useSearch();
  const { selectedInstance, instances, pendingInstanceId, selectInstance } = useTenantInstance();
  const link = runInstanceLink(
    pendingInstanceId,
    selectedInstance?.id ?? null,
    instances,
    "instance.read",
  );
  const linkBlocks = link.kind !== "none" && link.kind !== "same";
  const valid = isMcpWriteIntentId(id);
  const intent = useMcpWriteIntent(id, valid && !linkBlocks);

  useEffect(() => {
    if (link.kind !== "same") return;
    navigate(withoutInstanceParam(location, search), { replace: true });
  }, [link.kind, location, search, navigate]);

  if (!valid) {
    return (
      <div className="flex flex-col gap-4" data-testid="mcp-approve-page">
        <NoChangeHere testId="mcp-approve-invalid" title="This approval link is not valid" />
      </div>
    );
  }

  if (link.kind === "other" || link.kind === "no_access" || link.kind === "unresolved") {
    return (
      <div className="flex flex-col gap-4" data-testid="mcp-approve-page">
        <McpInstanceSwitchNotice
          link={link}
          subject="This change"
          onSwitch={selectInstance}
          testId="mcp-approve-instance-switch"
        />
      </div>
    );
  }

  let body: React.ReactNode;
  if (intent.isError) {
    body = isApiErrorCode(intent.error, "NOT_FOUND") ? (
      <NoChangeHere testId="mcp-approve-not-found" title="There is no change to approve here" />
    ) : (
      <SkyMessageBar
        intent="error"
        layout="multiline"
        testId="mcp-approve-load-error"
        title="We could not load this change"
        message={failureMessage(intent.error, "Reload the page to try again.")}
      />
    );
  } else if (intent.data) {
    body = <IntentView intent={intent.data} instanceName={selectedInstance?.name ?? ""} />;
  } else {
    body = (
      <SkyText size={300} color="muted" testId="mcp-approve-loading">
        Loading the change…
      </SkyText>
    );
  }
  return (
    <div className="flex max-w-3xl flex-col gap-4" data-testid="mcp-approve-page">
      {body}
    </div>
  );
}

/** One label and value of the "what will happen" list. */
function Fact({
  label,
  children,
  testId,
}: {
  label: string;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <>
      <dt>
        <SkyText size={300} weight="semibold">
          {label}
        </SkyText>
      </dt>
      <dd className="min-w-0 [overflow-wrap:anywhere]" data-testid={testId}>
        {children}
      </dd>
    </>
  );
}

/** An argument the AI app sent, as plain text: a list or an object as compact JSON. */
function argumentText(value: unknown): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function IntentView({
  intent,
  instanceName,
}: {
  intent: McpWriteIntentView;
  instanceName: string;
}) {
  const zone = useDisplayTz();
  const now = useNow();
  const phase = approvePhase(intent);
  const approve = useDecideMcpWriteIntent(intent.id, "approve");
  const decline = useDecideMcpWriteIntent(intent.id, "decline");
  const decisionError = useInlineError();
  const busy = approve.isPending || decline.isPending;
  const action = writeToolLabel(intent);
  const reason = writeReasonSentence(intent);
  const args = Object.entries(intent.args);

  function decide(which: "approve" | "decline") {
    decisionError.clear();
    const mutation = which === "approve" ? approve : decline;
    mutation.mutate(undefined, {
      onError: (error) => {
        decisionError.show(
          error,
          which === "approve" ? "The change was not approved." : "The change was not declined.",
        );
      },
    });
  }

  return (
    <>
      <div>
        <SkyText as="h2" size={400} weight="semibold" block testId="mcp-approve-title">
          {intent.clientName} asks to: {action}
        </SkyText>
        <SkyText as="p" size={300} color="muted" block className="mt-1">
          It asks as you, through {intent.grantName}. Nothing has happened yet: the change runs only
          if you approve it here.
        </SkyText>
      </div>

      <SkyCard className="flex flex-col gap-4 p-4">
        <SkyText as="h3" size={300} weight="semibold" block>
          What will happen
        </SkyText>
        <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-2">
          <Fact label="Change" testId="mcp-approve-action">
            <SkyText size={300}>{action}</SkyText>
          </Fact>
          {intent.summary.items.map((item) => (
            <Fact key={item.key} label={item.label} testId={`mcp-approve-summary-${item.key}`}>
              <SkyText size={300}>{summaryValueText(item.value)}</SkyText>
            </Fact>
          ))}
          {intent.coverage ? (
            <Fact label="Tables covered" testId="mcp-approve-coverage">
              <SkyText size={300}>{coverageSentence(intent.coverage)}</SkyText>
            </Fact>
          ) : null}
          <Fact label="Instance" testId="mcp-approve-instance">
            <SkyText size={300}>{instanceName}</SkyText>
          </Fact>
          <Fact label="AI app" testId="mcp-approve-client">
            <SkyText size={300}>{intent.clientName}</SkyText>
            <SkyText size={200} color="muted" block>
              {intent.grantName}
            </SkyText>
          </Fact>
          <Fact label="Asked" testId="mcp-approve-prepared">
            <SkyText size={300} title={instantHoverTitle(intent.preparedAt, zone)}>
              {formatInstant(intent.preparedAt, zone, "minutes", { named: true })}
            </SkyText>
            <SkyText size={200} color="muted" block testId="mcp-approve-age">
              {endedWhen(intent.preparedAt, zone, now)}
            </SkyText>
          </Fact>
          {intent.approvalExpiresAt ? (
            <Fact label="Link works until" testId="mcp-approve-expires">
              <SkyText size={300}>
                {formatInstant(intent.approvalExpiresAt, zone, "minutes", { named: true })}
              </SkyText>
            </Fact>
          ) : null}
        </dl>
        {reason ? (
          <SkyText as="p" size={300} block testId="mcp-approve-reason">
            <SkyText size={300} weight="semibold">
              Why it needs you:{" "}
            </SkyText>
            {reason}
          </SkyText>
        ) : null}
        {args.length > 0 ? (
          <SkyAccordion multiple size="small" testId="mcp-approve-args">
            <SkyAccordionItem value="args" heading="Exactly what the AI app sent">
              <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1">
                {args.map(([key, value]) => (
                  <Fact key={key} label={key} testId={`mcp-approve-arg-${key}`}>
                    <SkyText size={200} font="monospace" className="break-all">
                      {argumentText(value)}
                    </SkyText>
                  </Fact>
                ))}
              </dl>
            </SkyAccordionItem>
          </SkyAccordion>
        ) : null}
      </SkyCard>

      <PhaseNotice phase={phase} intent={intent} />

      <InlineErrorBar
        error={decisionError.error}
        title="That did not work"
        onDismiss={decisionError.clear}
        testId="mcp-approve-error"
      />

      {phase === "decide" ? (
        <>
          <SkyText as="p" size={300} block testId="mcp-approve-warning">
            Approve only if you asked {intent.clientName} for this change. Text the AI app read, in
            skyData or anywhere else, could have steered it. Your approval and the change are
            recorded in the audit trail.
          </SkyText>
          <div className="flex flex-wrap gap-2">
            <SkyButton
              appearance="primary"
              testId="mcp-approve-approve"
              loading={approve.isPending}
              disabled={busy}
              onClick={() => decide("approve")}
            >
              Approve
            </SkyButton>
            <SkyButton
              appearance="secondary"
              testId="mcp-approve-decline"
              loading={decline.isPending}
              disabled={busy}
              onClick={() => decide("decline")}
            >
              Decline
            </SkyButton>
          </div>
        </>
      ) : null}
    </>
  );
}

/** A run the change started, when the recorded result names one. */
function runIdOf(result: unknown): string | null {
  if (!result || typeof result !== "object") return null;
  const runId = (result as { runId?: unknown }).runId;
  return typeof runId === "string" && runId.length > 0 ? runId : null;
}

function PhaseNotice({ phase, intent }: { phase: ApprovePhase; intent: McpWriteIntentView }) {
  switch (phase) {
    case "decide":
      return null;
    case "expired":
      return (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          testId="mcp-approve-expired"
          title="This approval link has expired"
          message={`A link works for 24 hours. Nothing ran. Ask ${intent.clientName} to prepare the change again if you still want it.`}
        />
      );
    case "running":
      return (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          testId="mcp-approve-running"
          title="Approved. The change is running"
          message="This page shows how it ended in a moment."
        />
      );
    case "declined":
      return (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          testId="mcp-approve-declined"
          title="You declined this change"
          message={`Nothing ran. ${intent.clientName} is told when it asks how the change ended.`}
        />
      );
    case "failed":
      return (
        <SkyMessageBar
          intent="error"
          layout="multiline"
          testId="mcp-approve-failed"
          title="The change did not run"
          message={outcomeCodeSentence(intent.outcome?.code ?? null) ?? "Nothing was changed."}
        />
      );
    case "done": {
      const runId = runIdOf(intent.outcome?.result);
      return (
        <SkyMessageBar
          intent="success"
          layout="multiline"
          testId="mcp-approve-done"
          title="Approved and done"
          message={`The change ran as you. ${intent.clientName} sees the result when it asks.`}
          actions={
            runId ? (
              <Link
                href={absolutePath(`${ROUTES.runs}/${runId}`)}
                className="text-sm text-accent-text underline"
                data-testid="mcp-approve-run-link"
              >
                Open the run
              </Link>
            ) : undefined
          }
        />
      );
    }
  }
}
